import * as THREE from "three";

import { quatMul, quatRotate, type Quat } from "../math";
import type { ReposeSim } from "./mujoco";

const HEAD_POS = [0.05, 0, 0.4318] as const;
const HEAD_QUAT: Quat = [0.6533, 0.2706, -0.2706, -0.6533];
const HEAD_WIDTH = 112;
const HEAD_HEIGHT = 63;

interface RenderGeom {
  mesh: THREE.Mesh;
  id: number;
}

export class ReposeViewer {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(42, 16 / 9, 0.01, 30);
  private readonly headCamera = new THREE.PerspectiveCamera(42.5, 16 / 9, 0.02, 12);
  private readonly headTarget = new THREE.WebGLRenderTarget(HEAD_WIDTH, HEAD_HEIGHT, {
    depthBuffer: true,
    minFilter: THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
  });
  private readonly geoms: RenderGeom[] = [];
  private readonly attentionContext: CanvasRenderingContext2D;
  private readonly headPixels = new Uint8Array(HEAD_WIDTH * HEAD_HEIGHT * 4);
  private azimuth = 135 * Math.PI / 180;
  private elevation = 20 * Math.PI / 180;
  private distance = 3.9;
  private readonly lookAt = new THREE.Vector3(0.35, 0, 0.8);
  private readonly trackingTarget = new THREE.Vector3();
  private trackingReady = false;
  private drag: { x: number; y: number } | null = null;

  constructor(
    private readonly sim: ReposeSim,
    host: HTMLElement,
    private readonly attentionCanvas: HTMLCanvasElement,
  ) {
    const context = attentionCanvas.getContext("2d");
    if (!context) throw new Error("2D attention canvas is unavailable");
    this.attentionContext = context;
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.6));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.scene.background = new THREE.Color(0x4c4c52);
    host.append(this.renderer.domElement);

    const hemi = new THREE.HemisphereLight(0xffffff, 0x37383d, 1.45);
    const key = new THREE.DirectionalLight(0xffffff, 2.2);
    key.position.set(-2.5, -1.5, 5);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    key.shadow.camera.left = -3;
    key.shadow.camera.right = 3;
    key.shadow.camera.top = 3;
    key.shadow.camera.bottom = -3;
    this.scene.add(hemi, key);
    this.buildScene();
    this.installControls(host);
    this.resize();
    new ResizeObserver(() => this.resize()).observe(host);
  }

  private buildScene(): void {
    const model = this.sim.model;
    for (let id = 0; id < model.ngeom; id += 1) {
      if (Number(model.geom_group[id]) === 3) continue;
      const matId = Number(model.geom_matid[id]);
      const rgba = matId >= 0 ? model.mat_rgba : model.geom_rgba;
      const rgbaOffset = (matId >= 0 ? matId : id) * 4;
      const alpha = Number(rgba[rgbaOffset + 3]);
      if (alpha <= 0.001) continue;
      const geometry = this.geometryFor(id);
      if (!geometry) continue;
      const color = new THREE.Color(
        Number(rgba[rgbaOffset]),
        Number(rgba[rgbaOffset + 1]),
        Number(rgba[rgbaOffset + 2]),
      );
      const material = new THREE.MeshStandardMaterial({
        color,
        metalness: 0.08,
        roughness: 0.67,
        transparent: alpha < 0.999,
        opacity: alpha,
        side: THREE.DoubleSide,
      });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.matrixAutoUpdate = false;
      mesh.castShadow = Number(model.geom_group[id]) !== 0;
      mesh.receiveShadow = true;
      this.scene.add(mesh);
      this.geoms.push({ mesh, id });
    }
  }

  private geometryFor(id: number): THREE.BufferGeometry | null {
    const model = this.sim.model;
    const type = Number(model.geom_type[id]);
    const sx = Number(model.geom_size[id * 3]);
    const sy = Number(model.geom_size[id * 3 + 1]);
    const sz = Number(model.geom_size[id * 3 + 2]);
    if (type === 0) return new THREE.PlaneGeometry(40, 40);
    if (type === 2) return new THREE.SphereGeometry(sx, 18, 12);
    if (type === 3) {
      const geometry = new THREE.CapsuleGeometry(sx, 2 * sy, 6, 12);
      geometry.rotateX(Math.PI / 2);
      return geometry;
    }
    if (type === 4) {
      const geometry = new THREE.SphereGeometry(1, 18, 12);
      geometry.scale(sx, sy, sz);
      return geometry;
    }
    if (type === 5) {
      const geometry = new THREE.CylinderGeometry(sx, sx, 2 * sy, 20);
      geometry.rotateX(Math.PI / 2);
      return geometry;
    }
    if (type === 6) return new THREE.BoxGeometry(2 * sx, 2 * sy, 2 * sz);
    if (type !== 7) return null;

    const meshId = Number(model.geom_dataid[id]);
    const vertexStart = Number(model.mesh_vertadr[meshId]);
    const vertexCount = Number(model.mesh_vertnum[meshId]);
    const faceStart = Number(model.mesh_faceadr[meshId]);
    const faceCount = Number(model.mesh_facenum[meshId]);
    const positions = new Float32Array(vertexCount * 3);
    const indices = new Uint32Array(faceCount * 3);
    for (let i = 0; i < positions.length; i += 1) positions[i] = Number(model.mesh_vert[vertexStart * 3 + i]);
    for (let i = 0; i < indices.length; i += 1) indices[i] = Number(model.mesh_face[faceStart * 3 + i]);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setIndex(new THREE.BufferAttribute(indices, 1));
    geometry.computeVertexNormals();
    return geometry;
  }

  render(attention: Float32Array | null, query: number): Uint8Array {
    this.syncGeoms();
    this.positionOrbitCamera();
    this.positionHeadCamera();
    this.renderer.setRenderTarget(null);
    this.renderer.render(this.scene, this.camera);
    this.renderer.setRenderTarget(this.headTarget);
    this.renderer.render(this.scene, this.headCamera);
    this.renderer.readRenderTargetPixels(this.headTarget, 0, 0, HEAD_WIDTH, HEAD_HEIGHT, this.headPixels);
    this.renderer.setRenderTarget(null);
    this.paintAttention(attention, query);
    return this.headPixels;
  }

  /** Headless evaluation seam: render only the policy's actual head camera. */
  renderHead(): Uint8Array {
    this.syncGeoms();
    this.positionHeadCamera();
    this.renderer.setRenderTarget(this.headTarget);
    this.renderer.render(this.scene, this.headCamera);
    this.renderer.readRenderTargetPixels(
      this.headTarget,
      0,
      0,
      HEAD_WIDTH,
      HEAD_HEIGHT,
      this.headPixels,
    );
    this.renderer.setRenderTarget(null);
    return this.headPixels;
  }

  private syncGeoms(): void {
    const xpos = this.sim.data.geom_xpos;
    const xmat = this.sim.data.geom_xmat;
    for (const { mesh, id } of this.geoms) {
      const p = id * 3;
      const r = id * 9;
      mesh.matrix.set(
        xmat[r], xmat[r + 1], xmat[r + 2], xpos[p],
        xmat[r + 3], xmat[r + 4], xmat[r + 5], xpos[p + 1],
        xmat[r + 6], xmat[r + 7], xmat[r + 8], xpos[p + 2],
        0, 0, 0, 1,
      );
    }
  }

  private positionOrbitCamera(): void {
    const root = this.sim.rootPose().pos;
    this.trackingTarget.set(
      Number(root[0]) + 0.35,
      Number(root[1]),
      Math.max(0.55, Math.min(1.05, Number(root[2]) + 0.05)),
    );
    if (this.trackingReady) this.lookAt.lerp(this.trackingTarget, 0.12);
    else {
      this.lookAt.copy(this.trackingTarget);
      this.trackingReady = true;
    }
    const ce = Math.cos(this.elevation);
    this.camera.position.set(
      this.lookAt.x + this.distance * ce * Math.cos(this.azimuth),
      this.lookAt.y + this.distance * ce * Math.sin(this.azimuth),
      this.lookAt.z + this.distance * Math.sin(this.elevation),
    );
    this.camera.up.set(0, 0, 1);
    this.camera.lookAt(this.lookAt);
  }

  private positionHeadCamera(): void {
    const body = this.sim.data.body("torso_link");
    const rotated = quatRotate(body.xquat, HEAD_POS);
    const q = quatMul(body.xquat, HEAD_QUAT);
    this.headCamera.position.set(
      Number(body.xpos[0]) + rotated[0],
      Number(body.xpos[1]) + rotated[1],
      Number(body.xpos[2]) + rotated[2],
    );
    this.headCamera.quaternion.set(q[1], q[2], q[3], q[0]);
    body.delete();
  }

  private paintAttention(attention: Float32Array | null, query: number): void {
    const image = this.attentionContext.createImageData(HEAD_WIDTH, HEAD_HEIGHT);
    const map = attention ? attention.subarray(query * 21, query * 21 + 21) : null;
    let lo = Infinity;
    let hi = -Infinity;
    if (map) for (const value of map) { lo = Math.min(lo, value); hi = Math.max(hi, value); }
    for (let y = 0; y < HEAD_HEIGHT; y += 1) {
      for (let x = 0; x < HEAD_WIDTH; x += 1) {
        const source = ((HEAD_HEIGHT - 1 - y) * HEAD_WIDTH + x) * 4;
        const target = (y * HEAD_WIDTH + x) * 4;
        let r = this.headPixels[source] ?? 0;
        let g = this.headPixels[source + 1] ?? 0;
        let b = this.headPixels[source + 2] ?? 0;
        if (map && hi > lo) {
          const patchX = Math.min(6, Math.floor((x / HEAD_WIDTH) * 7));
          const patchY = Math.min(2, Math.floor((y / HEAD_HEIGHT) * 3));
          const heat = ((map[patchY * 7 + patchX] ?? lo) - lo) / (hi - lo);
          const hr = 255 * Math.min(1, 1.7 * heat);
          const hg = 255 * Math.max(0, 1 - Math.abs(heat - 0.64) * 2.6);
          const hb = 255 * Math.max(0, 1 - heat * 2.2);
          const alpha = 0.58 * heat;
          r = (1 - alpha) * r + alpha * hr;
          g = (1 - alpha) * g + alpha * hg;
          b = (1 - alpha) * b + alpha * hb;
        }
        image.data[target] = r;
        image.data[target + 1] = g;
        image.data[target + 2] = b;
        image.data[target + 3] = 255;
      }
    }
    const scratch = document.createElement("canvas");
    scratch.width = HEAD_WIDTH;
    scratch.height = HEAD_HEIGHT;
    scratch.getContext("2d")?.putImageData(image, 0, 0);
    this.attentionContext.imageSmoothingEnabled = true;
    this.attentionContext.drawImage(scratch, 0, 0, this.attentionCanvas.width, this.attentionCanvas.height);
  }

  resetCamera(): void {
    this.azimuth = 135 * Math.PI / 180;
    this.elevation = 20 * Math.PI / 180;
    this.distance = 3.9;
    this.trackingReady = false;
  }

  private installControls(host: HTMLElement): void {
    host.addEventListener("pointerdown", (event) => {
      this.drag = { x: event.clientX, y: event.clientY };
      host.setPointerCapture(event.pointerId);
    });
    host.addEventListener("pointermove", (event) => {
      if (!this.drag) return;
      this.azimuth -= (event.clientX - this.drag.x) * 0.008;
      this.elevation = Math.max(-0.1, Math.min(1.25, this.elevation + (event.clientY - this.drag.y) * 0.006));
      this.drag = { x: event.clientX, y: event.clientY };
    });
    host.addEventListener("pointerup", () => { this.drag = null; });
    host.addEventListener("wheel", (event) => {
      event.preventDefault();
      this.distance = Math.max(1.4, Math.min(7, this.distance * Math.exp(event.deltaY * 0.001)));
    }, { passive: false });
  }

  private resize(): void {
    const parent = this.renderer.domElement.parentElement;
    if (!parent) return;
    const width = Math.max(1, parent.clientWidth);
    const height = Math.max(1, parent.clientHeight);
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  dispose(): void {
    this.headTarget.dispose();
    this.renderer.dispose();
  }
}
